/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import { types, destroy, getEnv } from 'mobx-state-tree';
import CardModelUtil from './CardModelUtil';
import _ from 'underscore';
import CompareModel from 'features/compare/CompareModel';
import CometViewModel from 'features/comet/CometViewModel';
import FilterDockModel from 'features/filters/FilterDockModel';
import CardAPI from 'api/CardAPI';

//TODO: Need a better name for comet card
const SubType = types.union( types.enumeration(['compare','comet']), types.undefined );

export const CardLayoutModelExt = types
	.model('CardLayoutModelExt',{
		type: types.optional(types.string, 'exploreCard'),
		isHidden: types.optional(types.boolean, false),
		isFavourite: types.optional(types.boolean, false),
		subType: SubType
	})
	.volatile((self)=>({
		cardModelUtil: CardModelUtil,
		numberOfChildLayoutsReady: 0,
		showModalVisTypePicker: false,
		visDestination: null,
		compareModel:null,
		cometViewModel:null,
		filterDockModel: null,

		isDisplayed: false,
		filterDockInitialized: false,
		isNew: false,
		canvasTabs: null,
		canShowCanvasSidePanel: true,
		_mobxDisposeEvents: [],
		thumbnailsModel : null,
		isCurrentThumbnail: false,
		isDoneThumbnail: false,
		advancedVisualizationTypes: [],
		renderThumbnail: () => {
			if( self.thumbnailsModel ) {
				return self.thumbnailsModel.renderCard(self.id);
			}
		},
		api: null,
		getFeature: (featureName) => {
			let featureAPI = undefined;
			switch( featureName ) {
				case 'compare' :
					featureAPI = self.subType === 'compare' && self.compareModel.getAPI ?  self.compareModel.getAPI(): undefined;
					break;
				case 'driverAnalysis':
					featureAPI = self.isDriverAnalysis() && self.cometViewModel && self.cometViewModel.getAPI ? self.cometViewModel.getAPI() : undefined;
					break;
				case 'thumbnails':
					featureAPI = self.thumbnailsModel && self.thumbnailsModel.getAPI ? self.thumbnailsModel.getAPI(): undefined;
					break;
			}
			return featureAPI;
		},
		isNewCompareDnDFeature() {
			const env = getEnv(self);
			return env && env.isFeatureEnabled && !env.isFeatureEnabled('newCompareDnDEnabled', 'false');
		}
	}))
	.views(self => ({
		getVisualizations() {
			return self.cardModelUtil.getLayouts(self, self.boardModel);
		},
		getWidgets() {
			return self.items.map(
				visLayout => self.boardModel.getWidgetModel(visLayout.id)
			);
		},
		get isCompareEnabled() {
			return self.subType && self.subType === 'compare' || false;
		},

		//todo: we are going to create a comet card view.
		isDriverAnalysis() {
			const aVizs =  self.getVisualizations();
			if(	aVizs && aVizs.length > 0 ){
				const vis = aVizs[0];
				const sID = vis.widgetLayout ?  vis.widgetLayout.id : vis.placeholder.id;
				const aDriversAnalysisVizs = ['com.ibm.vis.rave2comet'];//Add more
				return aDriversAnalysisVizs.indexOf(self.boardModel.getWidgetModel(sID).visId) >= 0;
			}
			return false;
		}
	}))
	.actions(self => ({
		afterCreate() {
			self.getAdvancedVisualizationTypes(); // get usage types from vis def manager so it's available for when view's render() needs it
			self.createLayoutModel = self.createLayoutModel || getEnv(self).createLayoutModel;
			self.whitelistAttrs = ['id', 'from', 'css', 'items', 'style', 'type', 'title', 'templateName', 'relatedLayouts', 'clones', 'fillColor',  'disableScrollDrop', 'isHidden', 'isFavourite', 'subType', 'content'];
			//TODO delay instantiating compareModel, cometViewModel till they are needed
			self.compareModel = CompareModel.create({ statsModelMap:{}, selStatsModelMap:{} }, { isFeatureEnabled: getEnv(self).isFeatureEnabled });
			self.cometViewModel = CometViewModel.create( { selections:[], visIndexToShow:0 });
			self.cometViewModel.setCardModel(self);
			self.filterDockModel = FilterDockModel.create({ scopeId: self.id }, {
				dashboardApi: getEnv(self).dashboardApi,
				logger: getEnv(self).logger
			});
		},

		beforeDestroy() {
			self.compareModel && destroy(self.compareModel);
			self.cometViewModel && destroy(self.cometViewModel);
			self.filterDockModel && destroy(self.filterDockModel);

			// Remove the events.
			self._mobxDisposeEvents.forEach(dispose => dispose());

			if (self.api) {
				self.api.destroy();
				self.api = null;
			}
		},

		_initializeFilterDockIfNecessary(isDisplayed) {
			if (!self.filterDockInitialized && isDisplayed && !self.isDisplayed) {
				self.filterDockModel.initializeFilterDock();
				self.filterDockModel.switchDatasource(null);
				self.filterDockInitialized = true;
			}
		},
		/**
		 * Gets and stores advanced visualization types so it's ready for when view's render() needs it
		 */
		getAdvancedVisualizationTypes() {
			if(self.advancedVisualizationTypes.length > 0){
				return self.advancedVisualizationTypes;
			}
			const visDef = getEnv(self).dashboardApi.getFeature('VisDefinitions');
			const advancedTypes = [];
			// When opening a saved explore visDef is undefined that's why the check is needed
			visDef && visDef.getList().forEach((defElement) => {
				const usage = defElement.getProperty('usages');
				if (usage && usage.includes('Advanced')) {
					advancedTypes.push(defElement.getId());
				}
			});
			self.setAdvancedVisualizationTypes(advancedTypes);
			return self.advancedVisualizationTypes;
		},

		/**
		 * Checks if compare table should be shown
		 * Current conditions:
		 * 1. Widget is of advanced analytics type
		 * 2. Widget has no data(template or placeholder)
		 * 3. Combinations of conditions 1 and 2
		 * Compare table should be shown/hidden once all widgets in the layout are evaluated
		 * @param {Object} compareModel to check if widget has data
		 * @return {boolean}
		 */
		shouldShowCompareTable(compareModel) {
			const visualizations = self.getVisualizations();
			let unsupportedVisualizationCount = 0;
			if (visualizations && visualizations.length > 0) {
				visualizations.forEach(vis => {
					const widgetId = vis.widgetLayout ?  vis.widgetLayout.id : vis.placeholder.id;
					const advancedVizTypes = self.getAdvancedVisualizationTypes();
					if (advancedVizTypes.indexOf(self.boardModel.getWidgetModel(widgetId).visId) >= 0 || !compareModel.widgetHasData(widgetId)) {
						unsupportedVisualizationCount++;
					}
				});
			}
			return unsupportedVisualizationCount !== visualizations.length;
		},

		getAPI() {
			if(!self.api) {
				self.api = new CardAPI(self);
			}
			return self.api;
		},

		updateViewState(params) {
			self._initializeFilterDockIfNecessary(params.isDisplayed);
			self.isDisplayed = params.isDisplayed;
			self.canvasTabs = params.canvasTabs;
			self.canShowCanvasSidePanel = params.canShowCanvasSidePanel;
			if( !self.thumbnailsModel) {
				self.thumbnailsModel = params.thumbnailsModel;
			}
		},

		updateCanShowCanvasSidePanel(flag) {
			self.canShowCanvasSidePanel = flag;
		},

		setCurrentThumbnailInfo(isCurrentThumbnail, thumbnailsModel) {
			self.isCurrentThumbnail = isCurrentThumbnail;
			if( !self.thumbnailsModel) {
				self.thumbnailsModel = thumbnailsModel;
			}
		},

		setIsDoneThumbnail(flag) {
			self.isDoneThumbnail = flag;
		},

		setAdvancedVisualizationTypes(types) {
			self.advancedVisualizationTypes = types;
		},

		incrChildReadyCount() {
			self.numberOfChildLayoutsReady++;
		},

		incrStatsModelUpdateCount() {
			self.statsModelUpdateCount++;
		},
		setCardModelUtil(util) {
			self.cardModelUtil = util;
		},
		getTitle() {
			const separator = ' / ';
			const title = self.getVisualizations()
				.map(layout => {
					const widgetLayout = layout.widgetLayout;
					if(!widgetLayout) {
						return null;
					}
					const widgetModel = self.boardModel.getWidgetModel(widgetLayout.id);
					if(!widgetModel) {
						return null;
					}
					return widgetModel.name;
				})
				.filter(name => !!name)
				.join(separator);
			return title !== separator ? title : '';
		},
		toggleFavourite(state) {
			if (state === undefined) {
				self.isFavourite = !self.isFavourite;
			} else {
				self.isFavourite = state;
			}
		},
		toggleHidden(state) {
			if (state === undefined) {
				self.isHidden = !self.isHidden;
			} else {
				self.isHidden = state;
			}
		},
		setShowModalVisTypePicker(show, destination) {
			self.showModalVisTypePicker = show;
			self.visDestination = show ? destination : null;
		},
		setSelections(selections)	{
			self.cometViewModel.setSelections(selections);
		},
		placeWidgetInCard(widgetSpec, payloadData) {
			payloadData = _.isString(payloadData)? {
				undoRedoTransactionId: _.uniqueId(payloadData)
			} : payloadData;

			widgetSpec.parentId = self.id;  //appends widget under this layout
			widgetSpec.model.showTitle = undefined;
			widgetSpec.model.titleMode = 'smartTitle';

			self.boardModel.addWidget(widgetSpec, 'cardSender', payloadData);
		},

		//add data source, update modelRef, remove widget id
		_prepareWidgetSpec(fragmentSpec, payloadData) {
			const boardModel = self.boardModel;
			const dataSourcesFeature = boardModel.dashboardApi.getFeature('dataSources.deprecated');
			const sourceCollection = dataSourcesFeature.getSourcesCollection();
			const sourceIdMap = sourceCollection.addSourcesForPin(fragmentSpec.model, {
				payloadData: payloadData
			});
			const widget = fragmentSpec.model.widgets[Object.keys(fragmentSpec.model.widgets)[0]];
			const dataViews = widget.data ? widget.data.dataViews : null;
			if (dataViews) {
				dataViews.forEach(function(dataView) {
					if (sourceIdMap[dataView.modelRef]) {
						dataView.modelRef = sourceIdMap[dataView.modelRef];
					}
				});
			}
			const widgetSpec = {};
			delete widget.id;
			widgetSpec.model = widget;
			return widgetSpec;
		},

		_addDataSource(spec, options) {
			const dashboard = getEnv(self).dashboardApi;
			const dataSources = dashboard.getFeature('DataSources');
			const data = spec && spec.model && spec.model.data;
			if (data && data.dataViews && data.dataViews.length) {
				data.dataViews.forEach((view) => {
					if (view.model) {
						view.modelRef = dataSources.addDataSource(view.model, false, options);
						delete view.model;
					}
				});
			}
		},

		replaceWidgetInCard(spec, layoutId, payloadData) {
			payloadData = _.isString(payloadData)? {
				undoRedoTransactionId: _.uniqueId(payloadData)
			} : payloadData;

			let widgetSpec = JSON.parse( JSON.stringify(spec) );
			if (widgetSpec.specType && widgetSpec.specType === 'fragment') {
				widgetSpec = self._prepareWidgetSpec(spec, payloadData);
			}

			self._addDataSource(widgetSpec);

			// Preserve Content model for pinning
			const widgetContentMap = self.createContentModelMap(spec.model.layout);
			const widgetContentProperties = Object.values(widgetContentMap);
			if(widgetContentProperties.length > 0) {
				widgetSpec.layoutProperties = { content: widgetContentProperties[0] };
			}

			widgetSpec.parentId = self.id;
			widgetSpec.insertBefore = layoutId;  //adds widget in the place of layoutId layout
			widgetSpec.model.showTitle = undefined;
			widgetSpec.model.titleMode = 'smartTitle';
			// force setting label orientation and legend position prop in widget spec when compare enabled
			if(self.isCompareEnabled){
				const extraWidgetOptions = {
					properties: [{
						id: 'itemAxis.labels.layoutMode',
						value: 'stagger'
					}, {
						id: 'widget.legend.position',
						value: 'right'
					}]
				};
				Object.assign(widgetSpec.model, extraWidgetOptions);
			}
			self.boardModel.addWidget(widgetSpec, 'cardSender', payloadData);
			// deselect the placeholder widget before removing it
			const dashboardApi = getEnv(self).dashboardApi;
			dashboardApi && dashboardApi.getCanvas().deselectContent([layoutId]);
			self.boardModel.removeLayouts(layoutId, 'cardSender', payloadData);  //and removes layoutId layout
		},
		addStatsModels(widgetId, initialModel, initialSelModel, measureLabel, widgetAlias) {
			self.compareModel.addStatsModels(widgetId, initialModel, initialSelModel, measureLabel, widgetAlias);
		},
		setupStatsModels(widgetId, svc) {
			return self.compareModel.setupStatsModels(widgetId, svc);
		},
		setIsNew(isNew) {
			self.isNew = isNew;
		}
	}));



// WEBPACK FOOTER //
// ./src/js/core/CardLayoutModelExt.js